import { ViewChild, ViewChildren, QueryList, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { NbToastrService, NbDialogService } from '@nebular/theme';
import { saveAs } from 'file-saver/src/FileSaver';
import { MatTabGroup } from '@angular/material/tabs';
import { LocalDataSource } from 'ng2-smart-table';

import { format } from 'support';
import { CopyFitnessDay, CopyFitnessPlan } from 'app/BuildOps';

import { FitnessPlanDay, Short_WeekDays, WeekDays } from 'app/@core/interfaces/common/CalorieFriend/plan-day';
import {
  DEFAULT_DURATION,
  FitnessPlan,
  FitnessPlanData,
  ProgramTemplateData,
  TemplatePlan,
} from 'app/@core/interfaces/common/CalorieFriend/plan';
import { PatientData } from 'app/@core/interfaces/common/CalorieFriend/patients';
import { UsersService } from 'app/@core/backend/common/services/users.service';
import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { FitnessBlock } from 'app/@core/interfaces/common/CalorieFriend/fitnessItem';
import { TutorialIDs } from 'app/@core/interfaces/common/CalorieFriend/tutorial';
import { User } from 'app/@core/interfaces/common/users';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';

import { Labelling } from '../../general-text.component';
import { FormConstants } from '../../CustomUIOps';
import { FitnessPlanTemplateSelectComponent } from '../../Plans/PlanTemplate/ListSelection/plan-template-select.component';
import { Utility } from 'app/@core/utils/utility';
import { ProgramPlans } from 'app/@core/interfaces/common/CalorieFriend/program-template';

export class FitnessPlanDefComponentBase extends BaseComponentCanDeactivate implements OnInit {
  get IsDirty() {
    let childrenDirty = false;
    this.PlanDayComponents.forEach((c) => (childrenDirty = childrenDirty || c.IsDirty));
    this.PlanDayLabelComponents.forEach((c) => (childrenDirty = childrenDirty || c.IsDirty));
    return this.isDirty || childrenDirty;
  }

  resetDirty() {
    super.resetDirty();
    this.PlanDayComponents.forEach((c) => c.resetDirty());
    this.PlanDayLabelComponents.forEach((c) => c.resetDirty());
  }

  @ViewChildren('PlanDayLabel') PlanDayLabelComponents: QueryList<any>;
  @ViewChildren('PlanDay') PlanDayComponents: QueryList<any>;
  @ViewChild('PlanReport') PlanReport: any;
  @ViewChild('MealDays', { static: false }) MealDays: MatTabGroup;
  @ViewChild('MealContents', { static: false }) MealContents: any = null;

  readonly UntitledFitnessPlan: string = 'Untitled Workout Plan';

  PlanLabel = '';
  PlanNewLabel = 'New Plan';
  PlanEditLabel = 'Edit Plan';
  TemplateLabel = 'Template';
  PlanNameLabel = 'Plan Name';
  PlanActiveLabel = 'Set as Active';
  PlanDeactiveActiveLabel = 'Set as Inactive';
  ActiveLabel = $localize`:@@SetAsActive:Set as Active`;

  LoadFramTemplateLabel = 'Load from Template';
  NewDayLabel = 'Add New Day';
  NameLabel = 'Day';
  AcceptLabel = 'Save Plan';
  BackLabel = 'Back';
  ViewMealPlanLabel = 'Patient View';
  UserProfileLabel = 'Profile Info';
  TemplateDescriptionLabel = 'Template Description';
  TemplateNameLabel = 'Template Name';
  SaveAsPDFLabel = 'Save as PDF';
  DescriptionLabel = 'Description';
  PlanNotesLabel = 'Notes';
  SubmitToPatientLabel = 'Send Client View';
  DefaultDayTitle = 'Untitled Day';

  ProblemGenertingPDF = 'Problem occurre generating PDF Report. Try again. If problem continues contact support.';
  ProblemSendingToPatient = 'Problem sending patient view to patient.';
  SuccessSendingToPatient = 'Sent email to patient.';
  Created = 'Created';
  Updated = 'Updated';
  ProblemSavingPlan = 'Problem occured saving the Patient Plan';
  PatientName = '';
  PatientShortHandName = '';

  OriginalPatientName: string = '';
  IsPreferredUnitImperial: boolean = false;

  PatientID = null;
  NotificationToken: string = null;

  get PlanID() {
    return this.PlanIDSubject.value;
  }
  set PlanID(value: number) {
    this.PlanIDSubject.next(value);
  }
  PlanIDSubject = new BehaviorSubject<number>(null);

  dayId: number;
  ShowTemplateButton = false;
  ref: any = null;
  Plans: Array<FitnessPlan> = null;
  PlanActive: boolean = false;
  ThePlan: FitnessPlan;

  TheDays: Array<FitnessPlanDay> = null;

  PlanForm: FormGroup;
  DaySource: LocalDataSource;
  protected readonly unsubscribe$ = new Subject<void>();
  EditingTemplate = false;
  PlanView: boolean;
  ShowEmailSubmit: boolean = false;
  HidePatientInfo: boolean = false;

  IsEditingTemplatePublic: boolean = false;
  currentUserID: any;
  templateUserID: any;

  selectedPlan = null;
  LoadData = true;
  DontShowViewPlan: boolean = true;
  TheTemplateID: number;
  TheTemplateIsPublic: boolean;
  TheTemplateName = '';
  TheTemplateDescription = '';
  PlanDescriptionStatic = '';
  PlanNotesStatic = '';

  Labels: Labelling;

  PlanNotesWidth = '100%';
  PlanNotesHeight = '75px';

  FormConstants = FormConstants;

  SelectedDayIndex = 0;

  inTutorial: boolean = false;
  inFinalTutorial: boolean = false;
  selectTemplateSubject = new BehaviorSubject(null);
  currentUserSubject = new BehaviorSubject(null);

  showSpinner: boolean = false;
  contentLoaded: boolean = false;
  isPlanTemplate = false;
  planIndex;
  programTemplate: TemplatePlan<ProgramPlans>;

  /**
   * Ng2 smart table settings for Days (Plan Days)
   */
  DayTableSettings = {
    mode: 'external',
    actions: {
      position: 'right',
      add: false,
    },
    edit: {
      editButtonContent:
        '<img src="assets/icons/added_oultine_modify_pencil.svg" class="img-archive" width="24" height="24">',
    },
    delete: {
      deleteButtonContent: '<img src="assets/icons/trash-2-outline.svg" class="img-archive" width="24" height="24">',
    },
    columns: {
      name: { title: this.NameLabel, type: 'string', filter: false },
    },
  };

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private dialogService: NbDialogService,
    private planService: FitnessPlanData,
    private toasterService: NbToastrService,
    private usersService: UsersService,
    private router: Router,
    private patientsService: PatientData,
    private programTemplateService: ProgramTemplateData
  ) {
    super(dialogService);
    this.Labels = new Labelling();

    this.InitPlanDefForm();

    if (!this.PatientID) this.PatientID = this.route.snapshot.paramMap.get('PatientId');

    this.route.params.subscribe((params) => {
      this.PlanIDSubject.next(params['PlanId']);
    });
    /**
     * @description disabled by @intel
    if (!this.PlanID) this.PlanID = this.route.snapshot.paramMap.get('PlanId');
    if (!this.PlanID) this.PlanID = this.route.snapshot.queryParams.PlanId;
    */

    if (!this.PatientID) this.NotificationToken = this.route.snapshot.paramMap.get('NotificationToken');
    else this.ShowEmailSubmit = true;
  }

  get PlanName() {
    return this.PlanForm.get('PlanName');
  }
  get PlanDescription() {
    return this.PlanForm.get('PlanDescription');
  }
  get PlanNotes() {
    return this.PlanForm.get('PlanNotes');
  }
  get TemplateName() {
    return this.PlanForm.get('TemplateName');
  }
  get TemplateDescription() {
    return this.PlanForm.get('TemplateDescription');
  }

  get IsViewTemplate() {
    const isPublic = this.route.snapshot.queryParamMap.get('public') === 'true';
    return isPublic;
  }

  /**
   * Initializes the component.
   * - Initalizes the Plan Def form.
   * - Gets the Patient ID / Patient NotificationToken and Plan ID from the URL.
   * - Initializes the Plan (Create) or loads the Plan (Edit) including its PlanDays data.
   */
  ngOnInit(): void {
    const dayId = +this.route.snapshot.queryParamMap.get('dayId');
    if (!isNaN(dayId)) this.dayId = dayId;

    this.SetupPlan();
  }

  SetupPlan() {
    const loadUser = this.usersService.getCurrentUser();
    loadUser.subscribe((user) => {
      if (user != null) this.currentUserID = user.id;
      this.currentUserSubject.next(user);
    });

    this.TheDays = null;

    if (this.NotificationToken) {
      this.loadPatientData(this.NotificationToken);
    } else {
      if (this.PatientID !== null || this.PatientID > 0) {
        this.loadPatientData(this.PatientID);
      }
    }

    if (this.ThePlan === null || this.ThePlan === undefined) {
      this.ThePlan = {
        id: null,
        days: new Array<FitnessPlanDay>(),
        name: this.UntitledFitnessPlan,
        patientID: this.PatientID,
        createdDate: null,
        description: '',
        notes: '',
        active: false,
        zOrder: Utility.INT32_MAX,
        duration: DEFAULT_DURATION,
      };
      this.ShowTemplateButton = !this.PlanView;

      this.route.queryParams.subscribe((params) => {
        let planId = params['planId'];
        if (planId) {
          this.loadPlanFromTemplate(planId);
        }
      });
      this.route.params.subscribe((params) => {
        if (params['programTemplateId'] && params['planIndex']) {
          this.isPlanTemplate = true;
          this.planIndex = params['planIndex'];

          this.programTemplateService.GetTemplateById(params['programTemplateId']).subscribe((template) => {
            this.programTemplate = template;
            this.PreparePlan(template.programPlans.fitnessPlans[params['planIndex']]);
          });
        }
      });
    } else {
      this.PreparePlan(this.ThePlan);
    }

    if (!this.PlanView) this.PrepareWorkItemPublishSubscribe();

    this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source

    if (!this.EditingTemplate) {
      if (this.ThePlan.id !== null && this.ThePlan.id > 0) {
        this.PlanLabel = $localize`:@@EditPlan:Edit Plan`;
        this.DontShowViewPlan = false;
      } else {
        this.PlanLabel = $localize`:@@NewPlan:New Plan`;
      }
    } else {
      this.PlanLabel = this.TemplateLabel;
    }
  }

  loadPatientData(id) {
    if (id) {
      this.patientsService
        .get(id, null)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((patient) => {
          this.PatientShortHandName = patient.firstName[0] + '. ' + patient.lastName;
          this.OriginalPatientName = patient.name;
          this.IsPreferredUnitImperial = patient.isPreferredUnitImperial;
        });
    }
  }

  /**
   * Method to prepare subscription for individual meal items
   */
  PrepareWorkItemPublishSubscribe() {
    if (
      this.ThePlan != null &&
      this.ThePlan !== undefined &&
      this.ThePlan.days != null &&
      this.ThePlan.days !== undefined &&
      this.ThePlan.days.length > 0
    ) {
      for (const day of this.ThePlan.days) {
        if (day.blocks != null && day.blocks !== undefined && day.blocks.length > 0) {
          for (const block of day.blocks) {
            if (block.items != null && block.items !== undefined && block.items.length > 0) {
              for (const item of block.items) {
                // if (!isObservable(item.Update)) { item.Update = new Subject(); }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Method to initialize the Plan Definition form.
   */
  InitPlanDefForm() {
    this.PlanForm = this.fb.group({
      PlanName: this.fb.control(this.UntitledFitnessPlan, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(50),
      ]),
      PlanDescription: this.fb.control('', [Validators.maxLength(150)]),
      PlanNotes: this.fb.control('', [Validators.maxLength(500)]),

      TemplateName: this.fb.control('', [Validators.required, Validators.minLength(5), Validators.maxLength(50)]),
      TemplateDescription: this.fb.control('', [Validators.maxLength(50)]),
    });

    this.initForm(this.PlanForm);
  }

  /**
   * override the initForm function
   */
  initForm(form) {
    form.valueChanges.subscribe(() => {
      this.isDirty = true;
    });
  }

  onNameChange(event) {
    if (this.PlanName.value === '') {
      this.PlanName.setValue(this.UntitledFitnessPlan);
    }
  }

  onLabelFocusout() {
    const tempIndex = this.SelectedDayIndex;
    this.SelectedDayIndex = -1;
    setTimeout(() => {
      this.SelectedDayIndex = tempIndex;
    }, 0);
  }

  // Method to add a new day to the plan definition.
  NewDay() {
    this.isDirty = true;

    const day: FitnessPlanDay = {
      id: -1,
      planID: this.PlanID,
      blocks: new Array<FitnessBlock>(),
      name: this.DefaultDayTitle,
      notes: '',
      dayOfWeek: [],
      createMeal: true,
      order: this.ThePlan.days ? Math.max(...this.ThePlan.days.map((b) => b.order)) + 1 : 1,
    };

    this.UpdateDay(day);

    this.OrderDays(this.ThePlan.days.length - 1);
  }

  /**
   * Method to update the days list of the Plan with data of {day} object.
   * @param day PlanDay object
   */
  UpdateDay(day: FitnessPlanDay) {
    if (this.ThePlan.days == null || this.ThePlan.days === undefined) {
      this.ThePlan.days = new Array<FitnessPlanDay>();
    }

    this.ThePlan.days.push(day);
    this.TheDays = this.ThePlan.days;
    this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source
  }

  /**
   * Event handler of the 'Edit' action to edit an existing PlanDay.
   * Opens the PlanDayComponent in a pop up window and updates its result to the days list using UpdateDay function.
   * @param $event Event object
   */
  onEdit($event) {}

  /**
   * Event handler of the 'Delete' action to delete an existing PlanDay.
   * Deletes the plan day and updates the list.
   * @param $event Event object
   */
  onDelete($event) {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this day?' },
      })
      .onClose.subscribe((result) => {
        if (result) {
          if ($event !== null && $event !== undefined) {
            this.ThePlan.days = this.ThePlan.days.filter(
              (day) => day.id !== $event.data.id || day.name !== $event.data.name
            );
            this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source
          }
        }
      });
  }

  /**
   * Function to validate all input data for the plan definition.
   */
  IsValid(): Boolean {
    let Res: Boolean = false;

    if (this.Valid) {
      if (
        this.ThePlan != null &&
        this.ThePlan !== undefined &&
        this.ThePlan.days != null &&
        this.ThePlan.days !== undefined &&
        this.ThePlan.days.length > 0
      ) {
        for (const day of this.ThePlan.days) {
          Res = day.name != null && day.name !== undefined && day.name.trim().length > 0 ? true : false;
          if (!Res) break;
        }
      } else Res = true;
    }

    return Res;
  }

  /**
   * Function save the plan definition.
   * Checks if all data are valid and performs the save action.
   */
  SavePlan() {
    if (this.IsValid()) {
      if (!this.EditingTemplate && !this.programTemplate) {
        if (this.ThePlan.days !== null && this.ThePlan.days.length > 0) {
          this.ThePlan.name = this.PlanName.value;
          this.ThePlan.active = this.PlanActive;
          this.ThePlan.description = this.PlanDescription.value;
          this.ThePlan.notes = this.PlanNotes.value;

          this.ThePlan.patientID = this.PatientID;

          const UpdatePlan = CopyFitnessPlan(this.ThePlan, false, true);

          const isFromTutorial =
            this.route.snapshot.url.length > 2
              ? this.route.snapshot.url[2].toString().toLowerCase() === 'fromtutorial'
              : false;
          if (isFromTutorial) {
            UpdatePlan.active = true;
            UpdatePlan.duration = 12;
          }

          this.showSpinner = true;
          const observable =
            this.ThePlan === undefined ||
            (this.ThePlan !== undefined && (this.ThePlan.id == null || this.ThePlan.id <= 0))
              ? this.planService.create(UpdatePlan)
              : this.planService.update(UpdatePlan);

          this.resetDirty();

          observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
            (data) => {
              this.PlanID = this.ThePlan.id = data.id;
              this.handleSuccessResponse();

              if (isFromTutorial && UpdatePlan.active) {
                this.planService.submitPatientNotification(this.PatientID, data.id).subscribe(() => {});
              }

              this.showSpinner = false;

              this.router.navigate([`/pages/CalorieFriend/FitnessPlanEdit/${this.PlanID}`]);
            },
            (err) => {
              this.handleWrongResponse(err);
              this.showSpinner = false;
            }
          );
        }
      } else if (this.programTemplate) {
        this.ThePlan.name = this.PlanName.value;
        this.ThePlan.active = this.PlanActive;
        this.ThePlan.description = this.PlanDescription.value;
        this.ThePlan.notes = this.PlanNotes.value;
        this.programTemplate.programPlans.fitnessPlans[this.planIndex] = CopyFitnessPlan(this.ThePlan, false);
        this.showSpinner = true;
        this.programTemplateService
          .UpdateTemplate(this.programTemplate, false)
          .pipe(
            finalize(() => {
              this.showSpinner = false;
            })
          )
          .subscribe(
            (res) => {
              this.handleSuccessResponse();
            },
            (err) => {
              this.handleWrongResponse(err);
            }
          );
      } else {
        this.ThePlan.name = this.PlanName.value;
        this.ThePlan.description = this.PlanDescription.value;
        this.ThePlan.notes = this.PlanNotes.value;

        this.ThePlan.active = this.PlanActive;
        this.ThePlan.patientID = this.PatientID;

        this.UpdateTemplate(
          this.ThePlan,
          this.PlanName.value,
          this.PlanDescription.value,
          this.TheTemplateID,
          this.TheTemplateIsPublic
        );

        this.resetDirty();
      }
    }
  }

  /**
   * Function to update the template and its plan data.
   * Submits the data via API service.
   * @param Plan Plan object
   * @param TemplateName Template Name
   * @param Description Template Description
   * @param TemplateID Template Identifier
   */
  private UpdateTemplate(Plan, TemplateName, Description, TemplateID, IsPublic) {
    const Template = {
      id: TemplateID,
      name: TemplateName,
      description: Description,
      thePlan: CopyFitnessPlan(Plan),
      isPublic: IsPublic,
    };

    this.showSpinner = true;
    const observable = this.planService.UpdateTemplate(Template, false);

    observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
      () => {
        this.handleSuccessResponse();
        this.showSpinner = false;
        // this._location.back();
      },
      (err) => {
        this.handleWrongResponse(null);
        this.showSpinner = false;
      }
    );
  }

  /**
   * Method to close the container dialog if it is opened in pop up dialog
   * or navigates back to the PatientSheetView page
   */
  Back() {
    this.router.navigateByUrl(
      '/pages/CalorieFriend/PatientSheetView/' + this.PatientID.toString() + '?planType=workout'
    );
  }

  handleSuccessResponse() {
    if (this.PatientShortHandName.length > 0) {
      if (this.ThePlan.id === null) this.toasterService.success('', `Plan created for ${this.PatientShortHandName}`);
      else this.toasterService.success('', `Plan saved for ${this.PatientShortHandName}`);
    } else {
      if (this.programTemplate) {
        this.toasterService.success('', `Plan saved.`);
      }
      if (this.ThePlan.id === null) this.toasterService.success('', `Plan created.`);
      else this.toasterService.success('', `Plan saved.`);
    }
  }

  handleWrongResponse(data) {
    let message = '';
    if (data && data.error === 'DuplicatedPlanName') {
      message = $localize`:@@DuplicatedPlanName:There is already a plan with the same name.`;
    } else if (data?.error) {
      message = data?.error;
    } else {
      message = this.ProblemSavingPlan;
    }

    this.toasterService.danger('', message);
  }

  /**
   * Function to open PlanTemplateSelectComponent in a pop up dialog to select the template to load plan data from.
   */
  SelectTemplate(isFromTutorial: boolean = false, user: User = null) {
    this.selectTemplateSubject = new BehaviorSubject(null);

    this.dialogService
      .open(FitnessPlanTemplateSelectComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: !isFromTutorial,
        context: {
          isFromTutorial: isFromTutorial,
          user: user,
        },
      })
      .onClose.subscribe((data) => {
        this.SelectedPlan(data);
        this.selectTemplateSubject.next(data?.plan);
      });
  }

  loadPlanFromTemplate(id) {
    this.planService.GetTemplateById(id).subscribe((data) => {
      let selectedPlan = { plan: data.thePlan, appendToCurrentPlan: false };
      this.SelectedPlan(selectedPlan);
      this.SavePlan();
    });
  }

  /**
   * Function to apply the data from the template to the current plan.
   * @param Plan Plan object
   */
  SelectedPlan(data) {
    const Plan = data?.plan;

    if (Plan) {
      // make sure none of the IDs are set in the items
      // this is a new plan with new days and new meals
      if (Plan.days != null && Plan.days.length > 0) {
        for (const day of Plan.days) {
          day.id = 0;
          day.planID = 0;

          if (day.blocks != null && day.blocks.length > 0) {
            for (const meal of day.blocks) {
              meal.id = 0;
              meal.dayID = 0;
            }
          }
        }
      }

      if (data.appendToCurrentPlan) {
        for (const day of Plan.days) {
          day.order = this.ThePlan.days.length + 1;
          this.ThePlan.days.push(day);
        }
      } else {
        const currentId = this.ThePlan.id;
        this.ThePlan = {
          ...Plan,
          zOrder: this.ThePlan.zOrder,
          duration: this.ThePlan.duration,
          createdDate: this.ThePlan.createdDate,
          name: Plan.name ? Plan.name : this.ThePlan.name,
          notes: Plan.notes ? Plan.notes : this.ThePlan.notes,
          description: Plan.description ? Plan.description : this.ThePlan.description,
        };
        this.ThePlan.patientID = this.PatientID;
        this.ThePlan.id = currentId;

        this.PlanForm.patchValue({
          PlanName: this.ThePlan.name,
          PlanDescription: this.ThePlan.description,
          PlanNotes: this.ThePlan.notes,
        });
      }

      // this.PlanActive = this.ThePlan.active;
      //this.PlanActive = false;

      this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source
      this.OrderDays();
    }
  }

  /**
   * Function to refresh the plans list to notify changes of the plan.
   */
  private RefreshPlan() {
    this.TheDays = null;

    if (this.LoadData) {
      if (this.PlanID) {
        this.planService.get(this.PlanID).subscribe((data) => {
          this.ThePlan = data;
          this.PlanID = data.id;
          this.PatientID = data.patientID;

          this.PreparePlan(this.ThePlan);
        });
      }
    }
  }

  /**
   * Function to prepare the form to submit the plan definition.
   * @param ThisPlan Plan object
   */
  private PreparePlan(ThisPlan: FitnessPlan) {
    this.ThePlan = ThisPlan;

    if (this.ThePlan !== null && this.ThePlan !== undefined) {
      this.PlanForm.patchValue({
        PlanName: this.ThePlan.name,
        PlanDescription: this.ThePlan.description,
        PlanNotes: this.ThePlan.notes,
        TemplateName: this.TheTemplateName,
        TemplateDescription: this.TheTemplateDescription,
      });

      this.PlanActive = this.ThePlan.active;

      if (this.PlanActive) this.ActiveLabel = $localize`:@@SetAsInactive:Set as Inactive`;
      else this.ActiveLabel = $localize`:@@SetAsActive:Set as Active`;

      this.PlanDescriptionStatic = this.ThePlan.description;
      this.PlanNotesStatic = this.ThePlan.notes;

      this.PatientID = this.ThePlan.patientID;
      this.ShowTemplateButton = false;

      this.PrepareWorkItemPublishSubscribe();
      this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source
      this.TheDays = this.ThePlan.days;

      this.OrderDays();

      super.resetDirty();

      this.contentLoaded = true;
    }
  }

  /**
   * Event handler of the 'Change' event of the 'Plans' drop down list on the 'Patient View'.
   * Prepares the plan for the presentation.
   * @param Plan Plan object
   */
  PlanChanged(Plan) {
    this.router.navigate([`/PatientView/Patient/${this.NotificationToken}/${Plan.id}`]);
    this.PreparePlan(Plan);
  }

  /**
   * Event handler of 'Save As PDF' action on the 'Patient View' page.
   * Saves the selected plan definition as PDF.
   */
  SaveAsPDF() {
    let observable = null;

    if (this.NotificationToken != null && this.NotificationToken !== undefined)
      observable = this.planService.SaveAsPDFFromToken(this.NotificationToken, this.ThePlan.id);
    else if (this.PatientID !== null && this.PatientID !== undefined)
      observable = this.planService.SaveAsPDF(this.PatientID, this.ThePlan.id);

    if (observable != null) {
      observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
        (PDFData) => {
          saveAs(PDFData, format('PatientPlan_{0}.pdf', this.ThePlan.name));
        },
        (err) => {
          this.toasterService.danger('', this.ProblemGenertingPDF);
        }
      );
    }
  }

  /**
   * Event handler of the 'Patient View' action on the Plan definition page.
   * Navigates to the 'Patient View' page.
   */
  ViewPlan() {
    if (
      this.NotificationToken != null &&
      this.NotificationToken !== undefined &&
      ((this.PlanID != null && this.PlanID !== undefined) || this.ThePlan != null)
    ) {
      const PlanID = this.PlanID != null && this.PlanID !== undefined ? this.PlanID : this.ThePlan.id;

      this.router.navigate([
        `/pages/CalorieFriend/PatientMealPlanNotification/` + this.NotificationToken + '/' + PlanID.toString(),
      ]);

      this.ref.close();
    } else if (
      this.PatientID != null &&
      this.PatientID !== undefined &&
      ((this.PlanID != null && this.PlanID !== undefined) || this.ThePlan != null)
    ) {
      const PlanID = this.PlanID != null && this.PlanID !== undefined ? this.PlanID : this.ThePlan.id;

      this.router.navigate([
        `/pages/CalorieFriend/PatientMealPlan/` + this.PatientID.toString() + '/' + PlanID.toString(),
      ]);

      this.ref.close();
    }
  }

  /**
   * Event handler of the 'Profile Info' action on the 'Patient View' page.
   * Opens the Patient Profile component in a pop up dialog.
   */
  ViewPatientInfo() {}

  get Valid(): boolean {
    const Valid: boolean =
      this.PlanName &&
      this.PlanName.value &&
      this.PlanName.value.length > 0 &&
      this.ThePlan != null &&
      this.ThePlan !== undefined &&
      this.ThePlan.days != null &&
      this.ThePlan.days !== undefined &&
      this.ThePlan.days.length > 0;

    return Valid;
  }

  /**
   * Event handler of the 'Send Patient View' action of the 'Patient View' page.
   * Submits patient notification to send an e-mail to the patient.
   */
  SendPatientView() {
    const observable = this.planService.submitPatientNotification(this.PatientID, this.ThePlan.id);

    observable.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (Data) => {
        this.toasterService.success('', this.SuccessSendingToPatient);
      },
      (err) => {
        this.toasterService.danger('', this.ProblemSendingToPatient);
      }
    );
  }

  /**
   * Event handler of 'Set Active' action on the plan definition page.
   * Sets the current plan as the active one for the patient.
   */
  SetActive() {
    this.PlanActive = !this.PlanActive;

    if (this.PlanActive) this.ActiveLabel = $localize`:@@SetAsInactive:Set as Inactive`;
    else this.ActiveLabel = $localize`:@@SetAsActive:Set as Active`;

    this.isDirty = true;
  }

  /**
   * Checks if Day tab can be removed from the tab group
   */
  get CanRemoveDay() {
    if (
      this.ThePlan &&
      this.ThePlan.days &&
      this.ThePlan.days.length > 1 &&
      this.MealDays &&
      this.MealDays.selectedIndex >= 0
    )
      return true;
    return false;
  }

  /**
   * Removes the selected Day from the tab group and days list
   */
  RemoveDay() {
    let AskToRemove: boolean = true;
    const Remove: boolean = true;

    const index = this.MealDays.selectedIndex;

    if (this.ThePlan.days[index].id < 0) AskToRemove = false;

    if (AskToRemove) {
      this.dialogService
        .open(ConfirmMessageDialogComponent, {
          closeOnEsc: true,
          closeOnBackdropClick: true,
          context: {
            Message: $localize`:@@DeleteDay:Are you sure you want to delete the day?`,
          },
        })
        .onClose.subscribe((result) => {
          if (result) {
            this.RemoveDayCore(index);
          }
        });
    } else {
      this.RemoveDayCore(index);
    }
  }

  RemoveDayCore(index) {
    const t = this.ThePlan.days[index];

    this.ThePlan.days.splice(index, 1);
    this.TheDays = this.ThePlan.days;
    this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source

    if (this.MealDays.selectedIndex >= this.ThePlan.days.length) this.MealDays.selectedIndex--;
    this.SelectedDayIndex--;
    if (this.SelectedDayIndex < 0 && this.TheDays.length > 0) {
      this.SelectedDayIndex = 0;
    }

    this.isDirty = true;

    this.OrderDays();
  }

  /**
   * Removes the selected Day from the tab group and days list
   */
  CopyDay() {
    const AskToRemove: boolean = true;
    const Remove: boolean = true;

    const index = this.MealDays.selectedIndex;

    const original = this.ThePlan.days[index];
    const copied = CopyFitnessDay(original, true);
    copied.name = copied.name + ' Copy';
    (copied.order = Math.max(...this.ThePlan.days.map((b) => b.order)) + 1), this.ThePlan.days.push(copied);
    this.TheDays = this.ThePlan.days;
    this.DaySource = new LocalDataSource(this.ThePlan.days); // create the source

    this.OrderDays(this.TheDays.length - 1);
  }

  OrderUp() {
    const index = this.MealDays.selectedIndex;
    const original = this.TheDays[index];

    if (index > 0) {
      const prevDay = this.TheDays[index - 1];
      const tempOrder = prevDay.order;
      prevDay.order = original.order;
      original.order = tempOrder;
      this.OrderDays(this.SelectedDayIndex - 1);
    }
  }

  OrderDown() {
    const index = this.MealDays.selectedIndex;
    const original = this.TheDays[index];

    if (index < this.TheDays.length - 1) {
      const prevDay = this.TheDays[index + 1];
      const tempOrder = prevDay.order;
      prevDay.order = original.order;
      original.order = tempOrder;
      this.OrderDays(this.SelectedDayIndex + 1);
    }
  }

  OrderDays(selectedIndex = -1) {
    if (!this.ThePlan || !this.ThePlan.days) return;

    this.ThePlan.days.sort((a, b) => (a.order > b.order ? 1 : -1));
    let index = 0;
    this.ThePlan.days.forEach((a) => {
      a.order = ++index;
    });

    this.TheDays = [];
    setTimeout(() => {
      this.TheDays = this.ThePlan.days;
      selectedIndex = this.ThePlan.days.findIndex((x) => x.id === this.dayId);
      this.SelectedDayIndex = selectedIndex >= 0 ? selectedIndex : 0;
    }, 50);
  }

  getOrderLabel(order) {
    if (!order) {
      order = 1;
    }
    return order;
  }

  getDaysOfWeeksString(day: FitnessPlanDay) {
    if (!day.dayOfWeek) return '';
    const ordered = day.dayOfWeek.sort((a, b) => a - b);
    const assignedDays: string[] = ordered
      .map((x, i) => {
        if (ordered.length > 2) {
          return Short_WeekDays[x];
        } else {
          return WeekDays[x];
        }
      })
      .filter((x) => x);

    return assignedDays.length === 0 ? '' : `${assignedDays.join(', ')}`;
  }

  public TutorialIDs = TutorialIDs;
}
